import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const MIN_SIZE = 0.85;
const MAX_SIZE = 4;
const MIN_TRIM_SECONDS = 0.5;
const SEEK_TOLERANCE = 0.25;

const toSeconds = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : fallback;
};

const getPlaySeconds = (playbackState) => {
    const duration = playbackState?.duration || 10;
    return (playbackState?.progress || 0) * duration;
};

const getSyncData = (video, playSeconds, mediaDuration) => {
    const trimStart = toSeconds(video?.trimStart);
    const snapAt = Math.max(trimStart, toSeconds(video?.snapAt, trimStart));
    const formationAt = toSeconds(video?.formationAt, trimStart);
    const playStartAt = toSeconds(video?.playStartAt);
    const rawTrimEnd = toSeconds(video?.trimEnd);
    const syncStart = Math.max(trimStart, snapAt);
    const hasTrimEnd = rawTrimEnd > syncStart + MIN_TRIM_SECONDS;
    const invalidTrimEnd = rawTrimEnd > 0 && !hasTrimEnd;
    const clipEnd = hasTrimEnd ? rawTrimEnd : (mediaDuration > 0 ? mediaDuration : null);
    const activePlaySeconds = playSeconds - playStartAt;

    if (activePlaySeconds < 0) {
        return {
            phase: 'waiting',
            targetTime: formationAt,
            trimStart,
            trimEnd: rawTrimEnd,
            hasTrimEnd,
            invalidTrimEnd,
            syncStart,
            clipEnd
        };
    }

    const targetTime = Math.max(syncStart, snapAt + activePlaySeconds);
    if (clipEnd && targetTime >= clipEnd) {
        return {
            phase: 'ended',
            targetTime: clipEnd,
            trimStart,
            trimEnd: rawTrimEnd,
            hasTrimEnd,
            invalidTrimEnd,
            syncStart,
            clipEnd
        };
    }

    return {
        phase: 'active',
        targetTime,
        trimStart,
        trimEnd: rawTrimEnd,
        hasTrimEnd,
        invalidTrimEnd,
        syncStart,
        clipEnd
    };
};

export default function VideoPanel({
    video,
    videos = [],
    activeVideoId,
    playbackState,
    mode,
    onModeChange,
    onSelectVideo,
    onUpdate,
    onAudioNoteRecorded,
    onAudioNoteDeleted,
    onClear,
    readOnly
}) {
    const videoRef = useRef(null);
    const dragRef = useRef(null);
    const resizeRef = useRef(null);
    const recorderRef = useRef(null);
    const chunksRef = useRef([]);
    const playRequestRef = useRef(0);
    const [position, setPosition] = useState({ x: 28, y: 88 });
    const [isRecording, setIsRecording] = useState(false);
    const [recordingError, setRecordingError] = useState('');
    const [mediaDuration, setMediaDuration] = useState(0);
    const [syncUi, setSyncUi] = useState({ tone: 'idle', label: 'Ready' });

    const playSeconds = useMemo(() => getPlaySeconds(playbackState), [playbackState]);
    const playbackRate = clamp(Number(playbackState?.speed || 1), 0.25, 2);
    const syncData = useMemo(() => getSyncData(video, playSeconds, mediaDuration), [video, playSeconds, mediaDuration]);

    const setSyncState = useCallback((tone, label) => {
        setSyncUi(prev => (prev.tone === tone && prev.label === label ? prev : { tone, label }));
    }, []);

    const syncVideo = useCallback(() => {
        const el = videoRef.current;
        if (!video || video.type !== 'upload' || !el) return;

        el.muted = true;
        el.defaultMuted = true;
        el.playbackRate = playbackRate;

        if (el.readyState < 1) {
            setSyncState('loading', 'Loading');
            return;
        }

        const targetTime = Number.isFinite(syncData.targetTime) ? Math.max(0, syncData.targetTime) : 0;
        if (Math.abs(el.currentTime - targetTime) > SEEK_TOLERANCE) {
            try {
                el.currentTime = targetTime;
            } catch (err) {
                setSyncState('error', 'Seek blocked');
                return;
            }
        }

        if (syncData.phase === 'waiting') {
            el.pause();
            setSyncState('waiting', 'Waiting');
            return;
        }

        if (syncData.phase === 'ended') {
            el.pause();
            setSyncState('ended', 'Clip ended');
            return;
        }

        if (!playbackState?.isPlaying) {
            el.pause();
            setSyncState(syncData.invalidTrimEnd ? 'warning' : 'idle', syncData.invalidTrimEnd ? 'Trim ignored' : 'Paused');
            return;
        }

        if (!el.paused && !el.ended) {
            setSyncState(syncData.invalidTrimEnd ? 'warning' : 'active', syncData.invalidTrimEnd ? 'Trim ignored' : 'Synced');
            return;
        }

        const requestId = playRequestRef.current + 1;
        playRequestRef.current = requestId;
        setSyncState('loading', 'Starting');
        const playPromise = el.play();
        if (playPromise?.then) {
            playPromise
                .then(() => {
                    if (playRequestRef.current === requestId) {
                        setSyncState(syncData.invalidTrimEnd ? 'warning' : 'active', syncData.invalidTrimEnd ? 'Trim ignored' : 'Synced');
                    }
                })
                .catch((err) => {
                    if (err?.name === 'AbortError') return;
                    if (playRequestRef.current === requestId) setSyncState('error', 'Playback blocked');
                });
        }
    }, [video, playbackRate, playbackState?.isPlaying, syncData, setSyncState]);

    useEffect(() => {
        syncVideo();
    }, [syncVideo]);

    useEffect(() => {
        if (video?.type !== 'upload') return;
        setMediaDuration(0);
        setSyncState('loading', 'Loading');
    }, [video?.url, video?.type, setSyncState]);

    const onPointerDown = (e) => {
        if (mode !== 'floating') return;
        dragRef.current = {
            pointerId: e.pointerId,
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onPointerMove = (e) => {
        if (!dragRef.current) return;
        const panelWidth = Math.round(360 * clamp(Number(video?.size || 1), MIN_SIZE, MAX_SIZE));
        setPosition({
            x: clamp(e.clientX - dragRef.current.x, 8, Math.max(8, window.innerWidth - panelWidth)),
            y: clamp(e.clientY - dragRef.current.y, 70, Math.max(70, window.innerHeight - 240))
        });
    };

    const onPointerUp = () => {
        dragRef.current = null;
    };

    const onResizePointerDown = (e) => {
        if (readOnly) return;
        e.preventDefault();
        e.stopPropagation();
        resizeRef.current = {
            pointerId: e.pointerId,
            startX: e.clientX,
            startSize: clamp(Number(video?.size || 1), MIN_SIZE, MAX_SIZE)
        };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };

    const onResizePointerMove = (e) => {
        if (!resizeRef.current) return;
        const delta = (e.clientX - resizeRef.current.startX) / 360;
        onUpdate?.({ size: clamp(resizeRef.current.startSize + delta, MIN_SIZE, MAX_SIZE) });
    };

    const onResizePointerUp = () => {
        resizeRef.current = null;
    };

    if (!video || video.type !== 'upload') return null;

    const size = clamp(Number(video.size || 1), MIN_SIZE, MAX_SIZE);
    const panelStyle = mode === 'floating'
        ? { left: position.x, top: position.y, '--video-panel-width': `${Math.round(360 * size)}px` }
        : { '--video-panel-width': `${Math.round(360 * size)}px` };

    const progressEnd = syncData.clipEnd || mediaDuration;
    const progressPercent = progressEnd > syncData.syncStart
        ? clamp(((syncData.targetTime - syncData.syncStart) / (progressEnd - syncData.syncStart)) * 100, 0, 100)
        : 0;

    const readCurrentVideoTime = () => {
        if (videoRef.current) {
            return Number(videoRef.current.currentTime || 0);
        }
        return null;
    };

    const tagFormation = () => {
        const time = readCurrentVideoTime();
        if (time === null) return;
        onUpdate?.({ formationAt: time });
    };

    const tagSnap = () => {
        const time = readCurrentVideoTime();
        if (time === null) return;
        onUpdate?.({ snapAt: time });
    };

    const setTrimPoint = (key) => {
        if (!videoRef.current) return;
        onUpdate?.({ [key]: Number(videoRef.current.currentTime || 0) });
    };

    const startRecording = async () => {
        if (readOnly || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            setRecordingError('Audio recording is not available in this browser.');
            return;
        }

        try {
            setRecordingError('');
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            chunksRef.current = [];
            const recorder = new MediaRecorder(stream);
            recorderRef.current = recorder;
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };
            recorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
                onAudioNoteRecorded?.(blob);
                setIsRecording(false);
            };
            recorder.start();
            setIsRecording(true);
        } catch (err) {
            console.warn('Audio recording failed:', err);
            setRecordingError('Microphone permission was not available.');
            setIsRecording(false);
        }
    };

    const stopRecording = () => {
        if (recorderRef.current?.state === 'recording') {
            recorderRef.current.stop();
        }
    };

    const playAudioNote = () => {
        if (!video.audioNoteUrl) return;
        const audio = new Audio(video.audioNoteUrl);
        audio.play().catch(() => { });
    };

    const handleLoadedMetadata = () => {
        const el = videoRef.current;
        const duration = Number.isFinite(el?.duration) ? el.duration : 0;
        setMediaDuration(duration);
        setSyncState('idle', playbackState?.isPlaying ? 'Starting' : 'Ready');
        window.requestAnimationFrame(syncVideo);
    };

    return (
        <section className={`qr-video-panel ${mode === 'side' ? 'side' : 'floating'}`} style={panelStyle}>
            <div
                className="qr-video-panel-header"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <div className="qr-video-heading">
                    <div className="qr-video-panel-title">{video.label || video.fileName || 'Practice Video'}</div>
                    <div className="qr-video-panel-subtitle">
                        Play {playSeconds.toFixed(1)}s / Speed {playbackRate}x / Video {syncData.targetTime.toFixed(1)}s
                    </div>
                    <div className="qr-video-sync-row">
                        <span className={`qr-video-sync-chip ${syncUi.tone}`}>{syncUi.label}</span>
                        {syncData.hasTrimEnd && <span>Trim {syncData.trimStart.toFixed(1)}s-{syncData.trimEnd.toFixed(1)}s</span>}
                    </div>
                </div>
                <div className="qr-video-panel-actions">
                    <span className="qr-video-muted-chip" title="Video sound is muted">Muted</span>
                    <button type="button" onClick={() => onModeChange(mode === 'side' ? 'floating' : 'side')} title={mode === 'side' ? 'Float over field' : 'Side-by-side'}>
                        {mode === 'side' ? 'Float' : 'Side'}
                    </button>
                    {!readOnly && <button type="button" onClick={onClear} title="Remove video from this play">Remove</button>}
                </div>
            </div>

            {videos.length > 1 && (
                <div className="qr-video-switcher" role="tablist" aria-label="Attached videos">
                    {videos.map((item, index) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={item.id === activeVideoId}
                            className={item.id === activeVideoId ? 'active' : ''}
                            onClick={() => onSelectVideo?.(item.id)}
                            title={item.fileName || item.label}
                        >
                            <span>{index + 1}</span>
                            <strong>{item.label || item.fileName || `Video ${index + 1}`}</strong>
                        </button>
                    ))}
                </div>
            )}

            <div className="qr-video-frame">
                <video
                    ref={videoRef}
                    src={video.url}
                    playsInline
                    preload="auto"
                    muted
                    onLoadedMetadata={handleLoadedMetadata}
                    onCanPlay={syncVideo}
                    onPlaying={() => setSyncState(syncData.invalidTrimEnd ? 'warning' : 'active', syncData.invalidTrimEnd ? 'Trim ignored' : 'Synced')}
                    onWaiting={() => playbackState?.isPlaying && setSyncState('loading', 'Buffering')}
                    onError={() => setSyncState('error', 'Video error')}
                />
                <div className="qr-video-frame-badge">
                    <span className={`qr-video-dot ${syncUi.tone}`} />
                    {syncUi.label}
                </div>
                <div className="qr-video-progress-track" aria-hidden="true">
                    <span style={{ width: `${progressPercent}%` }} />
                </div>
            </div>

            <div className="qr-video-tagbar">
                <button type="button" disabled={readOnly} onClick={tagFormation} title="Tag the current video frame as the formation preview">
                    Tag Formation
                </button>
                <button type="button" disabled={readOnly} onClick={tagSnap} title="Tag the current video frame as the snap/start sync point">
                    Tag Snap
                </button>
            </div>

            <div className="qr-video-trim">
                <label>
                    Trim Start
                    <input
                        readOnly={readOnly}
                        type="number"
                        min="0"
                        step="0.1"
                        value={Number(video.trimStart || 0)}
                        onChange={(e) => onUpdate?.({ trimStart: Math.max(0, Number(e.target.value) || 0) })}
                    />
                </label>
                <button type="button" disabled={readOnly} onClick={() => setTrimPoint('trimStart')}>Set Start</button>
                <label>
                    Trim End
                    <input
                        readOnly={readOnly}
                        type="number"
                        min="0"
                        step="0.1"
                        value={Number(video.trimEnd || 0)}
                        onChange={(e) => onUpdate?.({ trimEnd: Math.max(0, Number(e.target.value) || 0) })}
                    />
                </label>
                <button type="button" disabled={readOnly} onClick={() => setTrimPoint('trimEnd')}>Set End</button>
            </div>

            <div className={`qr-video-status ${syncData.invalidTrimEnd ? 'warning' : ''}`}>
                Formation {Number(video.formationAt ?? video.trimStart ?? 0).toFixed(1)}s
                {' / '}
                Snap {Number(video.snapAt ?? video.startAt ?? video.trimStart ?? 0).toFixed(1)}s
                {syncData.invalidTrimEnd ? ' / Trim end ignored' : ''}
                {syncData.invalidTrimEnd && !readOnly && (
                    <button type="button" className="qr-video-inline-button" onClick={() => onUpdate?.({ trimEnd: 0 })}>
                        Clear End
                    </button>
                )}
            </div>

            <div className="qr-video-notes">
                <label className="qr-video-text-note">
                    General Note
                    <textarea
                        value={video.note || ''}
                        readOnly={readOnly}
                        placeholder="Type a coaching note..."
                        onChange={(e) => onUpdate?.({ note: e.target.value })}
                    />
                </label>
                <div className="qr-video-review-grid">
                    <label className="qr-video-text-note">
                        Went Right
                        <textarea
                            value={video.positives || ''}
                            readOnly={readOnly}
                            placeholder="What worked on this rep?"
                            onChange={(e) => onUpdate?.({ positives: e.target.value })}
                        />
                    </label>
                    <label className="qr-video-text-note">
                        Went Wrong
                        <textarea
                            value={video.negatives || ''}
                            readOnly={readOnly}
                            placeholder="What needs correction?"
                            onChange={(e) => onUpdate?.({ negatives: e.target.value })}
                        />
                    </label>
                </div>
                <div className="qr-video-audio-note">
                    <div className="qr-video-note-label">Recorded Note</div>
                    <div className="qr-video-note-actions">
                        {!isRecording ? (
                            <button type="button" onClick={startRecording} disabled={readOnly}>Record</button>
                        ) : (
                            <button type="button" onClick={stopRecording}>Stop</button>
                        )}
                        <button type="button" onClick={playAudioNote} disabled={!video.audioNoteUrl}>Play</button>
                        {video.audioNoteUrl && !readOnly && (
                            <button type="button" onClick={onAudioNoteDeleted}>Delete</button>
                        )}
                    </div>
                    {recordingError && <div className="qr-video-recording-error">{recordingError}</div>}
                    {video.audioNoteUrl && <div className="qr-video-recording-ready">Audio note saved</div>}
                </div>
            </div>

            {!readOnly && (
                <div
                    className="qr-video-resize-handle"
                    title="Drag to resize video"
                    onPointerDown={onResizePointerDown}
                    onPointerMove={onResizePointerMove}
                    onPointerUp={onResizePointerUp}
                    onPointerCancel={onResizePointerUp}
                />
            )}
        </section>
    );
}
